import { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from 'react'
import { supabase, recoveryMode, clearRecoveryMode } from '../lib/supabase'
import { setUser as setSentryUser } from '../lib/sentry'
import { clearImpersonator } from '../lib/impersonation'
import { clearActiveBrandSelection, clearActiveAccountSelection } from './useAccount'
import { invalidateBrandAccess } from '../lib/brandAccess'
import type { Session, User } from '@supabase/supabase-js'
import type { ReactNode } from 'react'

// Block admin accounts from the brand portal. Checked server-side via the
// is_admin_email RPC (reads app_metadata.is_admin in auth.users) before any
// sign-in or sign-up attempt.
async function assertNotAdmin(email: string) {
  const { data: isAdmin, error } = await supabase.rpc('is_admin_email', { input_email: email })
  if (error) throw error
  if (isAdmin) {
    throw new Error('You are not authorized for this role.')
  }
}

// Only allow brand accounts into the brand portal. Checked server-side via
// is_non_brand_email RPC (reads user_metadata.role in auth.users) before any
// sign-in or sign-up attempt. New accounts (email not yet registered) pass through.
async function assertBrandRole(email: string) {
  const { data: isNonBrand, error } = await supabase.rpc('is_non_brand_email', { input_email: email })
  if (error) throw error
  if (isNonBrand) {
    throw new Error('You are not authorized for this role.')
  }
}

/**
 * Auth state for the whole app, resolved ONCE.
 *
 * This used to be a plain hook, and all twelve call sites got their own copy of
 * it: twelve onAuthStateChange subscriptions and twelve getSession() calls, each
 * maintaining a separate-but-identical piece of state, with more added on every
 * navigation. Beyond the redundant work, concurrent getSession() calls on an
 * expired token contend for supabase-js's auth lock — the "Navigator LockManager"
 * noise this project already filters out in Sentry.
 *
 * useAuth() below now reads a context instead, so the API every caller sees is
 * unchanged; only the number of subscriptions is.
 */
function useAuthState() {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRecovery, setIsRecovery] = useState(recoveryMode.value)
  // Ref so the onAuthStateChange closure always reads the latest value
  // without needing to re-register the listener
  const recoveryRef = useRef(recoveryMode.value)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          // Handles both direct tab (hash) and cross-tab (BroadcastChannel)
          recoveryRef.current = true
          setIsRecovery(true)
          setUser(null)
          setSession(null)
          setLoading(false)
        } else if (event === 'USER_UPDATED') {
          // Password was reset — allow normal sign-in now
          recoveryRef.current = false
          clearRecoveryMode()
          setIsRecovery(false)
          setLoading(false)
        } else if (!recoveryRef.current) {
          setIsRecovery(false)
          setSession(session)
          setUser(session?.user ?? null)
          setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null)
          setLoading(false)
        } else {
          // SIGNED_IN fired right after PASSWORD_RECOVERY — stay in recovery mode
          setLoading(false)
        }
      }
    )

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!recoveryRef.current) {
        setSession(session)
        setUser(session?.user ?? null)
        setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    await assertNotAdmin(email)
    await assertBrandRole(email)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  const signUp = useCallback(async (email: string, password: string) => {
    await assertNotAdmin(email)
    await assertBrandRole(email)
    const { error } = await supabase.auth.signUp({ email, password, options: { data: { role: 'brand' } } })
    if (error) throw error
  }, [])

  const signOut = useCallback(async () => {
    // End any impersonation so a subsequent normal login in this tab isn't
    // mis-attributed to the admin.
    clearImpersonator()
    // Drop the persisted active-brand selection so the NEXT user to sign in on
    // this browser doesn't inherit a stale "inside brand X" state (which would
    // e.g. keep a fresh agency out of its lobby / show a phantom brand).
    clearActiveBrandSelection()
    // Same reasoning for the account choice: a user in two agencies leaves a
    // persisted account id behind, and the next person to sign in on this browser
    // must not inherit it (pickAccount would find no matching row and fall back,
    // but only after resolving against someone else's selection).
    clearActiveAccountSelection()
    // Drop the shared brand_user_access rows so nothing cached against the
    // outgoing user can be read after the session ends.
    invalidateBrandAccess()
    await supabase.auth.signOut()
    // No hard reload here: signOut() above already fires onAuthStateChange,
    // which flips `user`/`session` to null and instantly unmounts
    // AuthenticatedApp in favor of <Login/> (App.tsx's catch-all route) —
    // AuthenticatedApp fully unmounting is what drops any brand/account state.
    // A window.location.replace('/') used to run here too, which forced a full
    // page reload on top of that already-instant transition: it painted Login
    // once from the reactive update, then again after the reload's own loading
    // spinner — a visible login->loading->login flicker. history.replaceState
    // just resets the URL bar (in case sign-out happened from a nested route
    // like /campaigns) without re-triggering any of that.
    window.history.replaceState(null, '', '/')
  }, [])

  const getToken = useCallback(async () => {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  }, [])

  // Memoised: this is a context value now, so a fresh object each render would
  // re-render every consumer on any unrelated parent render.
  return useMemo(
    () => ({ session, user, loading, isRecovery, signIn, signUp, signOut, getToken }),
    [session, user, loading, isRecovery, signIn, signUp, signOut, getToken],
  )
}

export type AuthState = ReturnType<typeof useAuthState>

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthState()
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  // A hard failure, not a silent fallback: a component rendered outside the
  // provider would otherwise quietly get a permanently-null user and read as
  // "signed out", which is exactly the kind of bug that reaches production.
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
